import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import "./AskFameoAI.scss";
import Header from '../../components/Header/Header';
import MobileFooter from '../../components/MobileFooter/MobileFooter';
import Avatar from '../../components/Avatar/Avatar';
import ScrollToTop from '../../components/Hooks/ScrollToTop';
import { useAiQuota } from '../../components/Hooks/useAiQuota';
import { compressImageForAi } from '../../utils/compressImageForAi';
import axios from '../../components/Hooks/axios';

import { HiOutlineMenuAlt2 } from "react-icons/hi";
import { PiMagicWandFill } from "react-icons/pi";
import { IoSparkles, IoReloadOutline } from "react-icons/io5";
import { CiCirclePlus, CiSearch } from "react-icons/ci";
import { MdClose } from "react-icons/md";
import { IoMdSend } from "react-icons/io";
import { FaCopy, FaCheck } from "react-icons/fa6";

const AskFameoAI = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const incomingState = location?.state || null;
    const { user } = useSelector((state) => state.auth);
    const { quota, setQuota, history, setHistory, countdownText, isLimitReached } = useAiQuota();

    const [inputPrompt, setInputPrompt] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [copiedId, setCopiedId] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    // Messages stream for general multi-turn conversation
    const [messages, setMessages] = useState([]);
    const [activeHistoryIndex, setActiveHistoryIndex] = useState(null);

    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Responsive listener
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        if (messages.length > 0) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, loading]);

    // Handle "+ New Chat"
    const handleNewChat = () => {
        setMessages([]);
        setActiveHistoryIndex(null);
        setInputPrompt("");
        setSelectedImage(null);
        if (isMobile) setIsSidebarOpen(false);
    };

    // Handle selecting a history item
    const handleSelectHistoryItem = (item, idx) => {
        setActiveHistoryIndex(idx);
        const promptText = item.prompt || "Conversation";
        const replyText = item.caption || "";

        setMessages([
            {
                id: `user-${idx}-${Date.now()}`,
                role: "user",
                content: promptText,
                timestamp: item.createdAt || new Date(),
            },
            {
                id: `ai-${idx}-${Date.now()}`,
                role: "assistant",
                content: replyText,
                timestamp: item.createdAt || new Date(),
            },
        ]);

        if (isMobile) setIsSidebarOpen(false);
        toast.info("Loaded chat from history!");
    };

    // Copy response text
    const handleCopy = (text, msgId) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedId(msgId);
        toast.success("Copied to clipboard!");
        setTimeout(() => setCopiedId(null), 2000);
    };

    // General Conversation Handler (Multi-turn chat)
    const handleSendMessage = async (overridePrompt = null) => {
        const textToSend = overridePrompt !== null ? overridePrompt : inputPrompt.trim();

        if (!textToSend && !selectedImage) {
            toast.info("Please type a message or attach an image.");
            return;
        }

        if (isLimitReached) {
            toast.warn(`Daily AI limit reached (15/15). Resets in ${countdownText}`);
            return;
        }

        const userMsgId = `user-${Date.now()}`;
        const aiMsgId = `ai-${Date.now()}`;
        const previewUrl = selectedImage instanceof Blob || selectedImage instanceof File
            ? URL.createObjectURL(selectedImage)
            : null;

        // Current turn user message
        const newUserMessage = {
            id: userMsgId,
            role: "user",
            content: textToSend,
            image: previewUrl,
            timestamp: new Date(),
        };

        // Loading placeholder for AI
        const loadingMessage = {
            id: aiMsgId,
            role: "assistant",
            loading: true,
            timestamp: new Date(),
        };

        // Snapshot previous turns for conversational context
        const conversationHistory = messages
            .filter((m) => !m.loading)
            .map((m) => ({
                role: m.role,
                content: m.content,
            }));

        setMessages((prev) => [...prev, newUserMessage, loadingMessage]);
        setInputPrompt("");
        const imageToUpload = selectedImage;
        setSelectedImage(null);
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("prompt", textToSend);
            formData.append("messages", JSON.stringify(conversationHistory));

            if (imageToUpload) {
                const compressed = await compressImageForAi(imageToUpload, 512);
                formData.append("image", compressed);
            }

            // Call the general chat endpoint with fallback to generate
            let resData = null;
            try {
                const { data } = await axios.post("/ask-fameo/chat", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                resData = data;
            } catch (chatErr) {
                // If /chat endpoint isn't supported yet, seamlessly fallback to /generate
                const { data } = await axios.post("/ask-fameo/generate", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                resData = data;
            }

            if (resData?.status) {
                const replyText =
                    resData.reply ||
                    resData.data?.captions?.[0]?.text ||
                    "I'm here to help! Let me know what you'd like to chat about or explore next.";

                setMessages((prev) =>
                    prev.map((msg) => {
                        if (msg.id === aiMsgId) {
                            return {
                                id: aiMsgId,
                                role: "assistant",
                                loading: false,
                                content: replyText,
                                timestamp: new Date(),
                            };
                        }
                        return msg;
                    })
                );

                if (resData.quota) {
                    setQuota(resData.quota);
                }

                // Append to local history list
                const newHistoryItem = {
                    prompt: textToSend || "General conversation",
                    caption: replyText.slice(0, 150),
                    hashtags: [],
                    createdAt: new Date(),
                };
                setHistory((prev) => [newHistoryItem, ...(prev || [])]);
            } else {
                toast.error(resData?.message || "Failed to receive response.");
                setMessages((prev) => prev.filter((m) => m.id !== aiMsgId));
            }
        } catch (err) {
            setMessages((prev) => prev.filter((m) => m.id !== aiMsgId));
            if (err.response?.status === 429) {
                const q = err.response.data?.quota;
                if (q) setQuota(q);
                toast.error(err.response.data?.message || "Daily AI limit reached!");
            } else {
                toast.error(err.response?.data?.message || "Failed to send message. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };



    // Filter history items by search query
    const filteredHistory = (history || []).filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.prompt?.toLowerCase().includes(q) ||
            item.caption?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="App">
            <div className="MainContainer">
                <ScrollToTop />
                {!isMobile && <Header />}

                <div className="ask-fameo-ai-wrapper">
                    {/* Left Sidebar: Chat History */}
                    <aside className={`ask-ai-sidebar ${isMobile && isSidebarOpen ? "open" : ""}`}>
                        <div className="sidebar-header">
                            <button
                                type="button"
                                className="new-chat-btn"
                                onClick={handleNewChat}
                            >
                                <CiCirclePlus size={22} />
                                <span>New Chat</span>
                            </button>
                            {isMobile && (
                                <button
                                    type="button"
                                    className="close-sidebar-btn"
                                    onClick={() => setIsSidebarOpen(false)}
                                >
                                    <MdClose size={22} />
                                </button>
                            )}
                        </div>

                        {/* Search Bar */}
                        <div className="sidebar-search">
                            <CiSearch />
                            <input
                                type="text"
                                placeholder="Search history..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <MdClose
                                    className="clear-icon"
                                    onClick={() => setSearchQuery("")}
                                />
                            )}
                        </div>

                        {/* Section Header */}
                        <div className="history-section-title">
                            <span>Chat History</span>
                        </div>

                        {/* History List */}
                        <div className="history-list">
                            {filteredHistory.length > 0 ? (
                                filteredHistory.map((item, idx) => {
                                    const isActive = activeHistoryIndex === idx;
                                    const previewText = item.prompt || item.caption || "Chat message";
                                    return (
                                        <div
                                            key={idx}
                                            className={`history-item ${isActive ? "active" : ""}`}
                                            onClick={() => handleSelectHistoryItem(item, idx)}
                                        >
                                            <div className="item-header">
                                                <span className="tag chat-tag">
                                                    <IoSparkles size={11} /> AI CHAT
                                                </span>
                                                <span className="timestamp">
                                                    {item.createdAt
                                                        ? new Date(item.createdAt).toLocaleDateString(undefined, {
                                                              month: "short",
                                                              day: "numeric",
                                                          })
                                                        : "Recent"}
                                                </span>
                                            </div>
                                            <p className="item-preview">{previewText}</p>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="history-empty">
                                    <IoSparkles className="empty-sparkle" />
                                    <p>
                                        {history && history.length > 0
                                            ? "No matches found"
                                            : "Your conversation history will appear here."}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Sidebar Quota Badge */}
                        <div className="sidebar-quota-pill">
                            <div className="quota-text">
                                <IoSparkles />
                                {isLimitReached ? (
                                    <span>Resets in: {countdownText}</span>
                                ) : (
                                    <span>Credits: {quota.remaining} / {quota.limit}</span>
                                )}
                            </div>
                        </div>
                    </aside>

                    {/* Mobile Sidebar Overlay Backdrop */}
                    {isMobile && isSidebarOpen && (
                        <div
                            className="sidebar-backdrop"
                            onClick={() => setIsSidebarOpen(false)}
                        />
                    )}

                    {/* Center Conversation Panel */}
                    <main className="ask-ai-center">
                        {/* Conversation Header */}
                        <header className="center-header">
                            <div className="left-controls">
                                {isMobile && (
                                    <button
                                        type="button"
                                        className="mobile-toggle-btn"
                                        onClick={() => setIsSidebarOpen(true)}
                                        title="View chat history"
                                    >
                                        <HiOutlineMenuAlt2 size={22} />
                                    </button>
                                )}
                                <div className="title-area">
                                    <div className="ai-badge-circle">
                                        <IoSparkles />
                                    </div>
                                    <div className="title-text">
                                        <h1>Ask Fameo AI</h1>
                                        <span className="subtitle">General AI Assistant •</span>
                                    </div>
                                </div>
                            </div>

                            <div className="right-controls">
                                {/* <button
                                    type="button"
                                    className="caption-mode-btn"
                                    onClick={() => navigate("/AskFameo")}
                                    title="Open Post Caption Studio"
                                >
                                    
                                </button> */}

                                {/* Live Quota Pill */}
                                <div
                                    className="credits-pill"
                                    style={{
                                        background: isLimitReached ? "rgba(239, 68, 68, 0.12)" : "rgba(166, 83, 157, 0.12)",
                                        color: isLimitReached ? "#ef4444" : "#a6539d",
                                    }}
                                >
                                    <IoSparkles />
                                    {isLimitReached ? (
                                        <span>Resets: {countdownText}</span>
                                    ) : (
                                        <span>{quota.remaining} / {quota.limit} Credits</span>
                                    )}
                                </div>
                            </div>
                        </header>

                        {/* Center Messages Stream */}
                        <div className="messages-stream">
                            {messages.length === 0 ? (
                                /* Empty Chat State: Welcome Hero & 4 Creative Suggestions */
                                <div className="empty-chat-state">
                                    <div className="hero-section">
                                        <div className="hero-glow-icon">
                                            <IoSparkles />
                                        </div>
                                        <h2>How can Ask Fameo AI help you today?</h2>
                                        <p>
                                            Ask any question, brainstorm creative concepts, draft content, or converse freely with Fameo-X's AI companion.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                /* Active Chat Messages Stream */
                                <div className="messages-container">
                                    {messages.map((msg, index) => {
                                        if (msg.role === "user") {
                                            return (
                                                <div key={msg.id || index} className="chat-message user-turn">
                                                    <div className="message-bubble user-bubble">
                                                        {msg.image && (
                                                            <div className="attached-image-thumb">
                                                                <img src={msg.image} alt="User attachment" />
                                                            </div>
                                                        )}
                                                        <p>{msg.content}</p>
                                                    </div>
                                                    <div className="message-avatar">
                                                        <Avatar
                                                            src={user?.ProfilePic}
                                                            name={user?.displayName || user?.username || "You"}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        }

                                        /* Assistant Loading */
                                        if (msg.loading) {
                                            return (
                                                <div key={msg.id || index} className="chat-message assistant-turn">
                                                    <div className="message-avatar ai-avatar pulsing">
                                                        <IoSparkles />
                                                    </div>
                                                    <div className="message-bubble assistant-bubble loading-bubble">
                                                        <div className="typing-dots">
                                                            <span></span>
                                                            <span></span>
                                                            <span></span>
                                                        </div>
                                                        <p>Ask Fameo AI is thinking...</p>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        /* Assistant Response */
                                        return (
                                            <div key={msg.id || index} className="chat-message assistant-turn">
                                                <div className="message-avatar ai-avatar">
                                                    <IoSparkles />
                                                </div>

                                                <div className="message-content-wrapper">
                                                    <div className="message-bubble assistant-bubble">
                                                        <div className="reply-body">
                                                            <p className="reply-text">{msg.content}</p>
                                                        </div>

                                                        {/* Actions Toolbar */}
                                                        <div className="assistant-actions">
                                                            <button
                                                                type="button"
                                                                className="action-btn regenerate"
                                                                onClick={() => {
                                                                    const prevUserMsg = messages
                                                                        .slice(0, index)
                                                                        .reverse()
                                                                        .find((m) => m.role === "user");
                                                                    handleSendMessage(prevUserMsg ? prevUserMsg.content : msg.content);
                                                                }}
                                                                disabled={loading || isLimitReached}
                                                            >
                                                                <IoReloadOutline />
                                                                <span>Regenerate</span>
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="action-btn copy"
                                                                onClick={() => handleCopy(msg.content, msg.id)}
                                                            >
                                                                {copiedId === msg.id ? (
                                                                    <>
                                                                        <FaCheck color="#10b981" />
                                                                        <span style={{ color: "#10b981" }}>Copied!</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FaCopy />
                                                                        <span>Copy</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </div>

                        {/* Pinned Bottom Input Area */}
                        <div className="center-input-wrapper">
                            {selectedImage && (
                                <div className="selected-media-chip">
                                    <span className="chip-icon">📷</span>
                                    <span className="chip-text">{selectedImage.name}</span>
                                    <MdClose
                                        className="chip-remove"
                                        onClick={() => setSelectedImage(null)}
                                    />
                                </div>
                            )}

                            <div className="input-bar">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                            const file = e.target.files[0];
                                            setSelectedImage(file);
                                            toast.info("Image attached for discussion!");
                                        }
                                    }}
                                />

                                <button
                                    type="button"
                                    className="attach-btn"
                                    title="Attach image (optional)"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <CiCirclePlus size={28} />
                                </button>

                                <input
                                    ref={textareaRef}
                                    type="text"
                                    placeholder={
                                        isLimitReached
                                            ? `Daily limit reached. Resets in ${countdownText}`
                                            : "Message Ask Fameo AI... (Ask anything, brainstorm, or chat)"
                                    }
                                    value={inputPrompt}
                                    onChange={(e) => setInputPrompt(e.target.value)}
                                    disabled={loading || isLimitReached}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                />

                                <button
                                    type="button"
                                    className="send-btn"
                                    onClick={() => handleSendMessage()}
                                    disabled={
                                        loading ||
                                        isLimitReached ||
                                        (!inputPrompt.trim() && !selectedImage)
                                    }
                                >
                                    <IoMdSend />
                                </button>
                            </div>

                            <div className="input-disclaimer">
                                <span>Ask Fameo AI can make mistakes. Verify important information. • 15 free credits daily</span>
                            </div>
                        </div>
                    </main>
                </div>

                {isMobile && <MobileFooter />}
            </div>
        </div>
    );
};

export default AskFameoAI;
